import { Box, Grid, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useProSidebar } from "react-pro-sidebar";
import { useLocation, useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { scenarioSetKeys, stepKeys } from "../../../../api/queryKeys";
import CustomTooltip from "../../../../components/CustomTooltip";
import LoadingIcon from "../../../../components/LoadingIcon";
import Pagination from "../../../../components/Pagination";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { PageDisplayMode } from "../../../../utils/constants";
import { icons } from "../../../stack/environment/icons/iconExport";
import StepDrawer from "../../steps/components/StepDrawer";
import ScenariosSetCards from "./ScenarioSetCard/ScenariosSetCards";
import TopImpactRemediations from "./TopImpactTables/TopImpactRemediations";
import TopImpactSteps from "./TopImpactTables/TopImpactSteps";

export default function ScenariosTable({
  setSelectedScenarioSet,
  filteredScenariosSets,
  isLoadingScenarioSets,
  scenariosSetsRowsCard,
  fullScreen,
  setDisplayMode,
  resetFilters,
}) {
  const colors = useColors();
  const { collapseSidebar } = useProSidebar();
  useEffect(() => {
    collapseSidebar(true);
  }, []);

  const [isScanLoading, setIsScanLoading] = useState(false);
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [currentRows, setCurrentRows] = useState([]);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [entityTypeFilter, setEntityTypeFilter] = useState(null);
  const { trackEvent, fetchAllSteps } = useAPI();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const rowsPerPage = 10;
  const totalPages = Math.ceil(scenariosSetsRowsCard?.length / rowsPerPage);

  const onStepDrawerClose = () => {
    trackEvent("Step Drawer closed from Top Impact Steps");
    setSelectedStepId(null);
    const params = new URLSearchParams(location.search);
    params.delete("step_id");
    setSearchParams(params.toString());
  };
  useEffect(() => {
    // refetch data for selected asset id when scan is done
    if (!isScanLoading) {
      queryClient.invalidateQueries({
        queryKey: scenarioSetKeys.all,
      });
    }
  }, [isScanLoading]);
  const { data: steps, isLoading: stepsIsLoading } = useQuery({
    queryKey: stepKeys.all,
    queryFn: fetchAllSteps,
    select: (data) =>
      data.data
        .filter((step) => !step.is_virtual)
        .map((step) => ({ ...step, id: step.step_id })),
  });

  useEffect(() => {
    setCurrentRows(
      scenariosSetsRowsCard?.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      )
    );
  }, [currentPage, scenariosSetsRowsCard]);

  useEffect(() => {
    const localSearchParams = new URLSearchParams(location.search);
    const stepsState = localSearchParams.get("steps");
    if (stepsState !== "open") {
      const step_id = localSearchParams.get("step_id");

      if (step_id && steps && !stepsIsLoading) {
        if (!Number.isNaN(Number(step_id))) {
          let step = steps.find((step) => step.step_id.toString() === step_id);
          if (step) {
            setSelectedStepId(step_id);
            const entity_type_filter =
              localSearchParams.get("entity_type_filter");
            if (entity_type_filter) {
              setEntityTypeFilter(entity_type_filter);
            }
          }
        }
      } else {
        setSelectedStepId(null);
      }
    }
  }, [location, steps, stepsIsLoading]);
  return isLoadingScenarioSets ? (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="100%"
    >
      <LoadingIcon size={16} />
    </Box>
  ) : (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <Grid container justifyContent={"space-between"}>
        <Grid item xs={12}>
          <Grid container justifyContent={"space-between"} gap={"2px"}>
            <Grid
              item
              xs={fullScreen ? 7 : 12}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"space-between"}
              sx={{
                backgroundColor: colors.common_white,
                boxShadow: fullScreen
                  ? `-4px 3px 4px 0px ${colors.grey_20percent} inset`
                  : `0px 2px 7px 2px ${colors.grey_20percent} inset`,
                borderRadius: fullScreen
                  ? "0px 24px 0 0 "
                  : "24px 24px 0px 0px",
                position: "relative",
              }}
            >
              {fullScreen && currentRows.length > 0 && (
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  gap={1}
                  alignItems={"center"}
                  justifyContent={"flex-end"}
                  sx={{ cursor: "pointer", color: colors.primary_main }}
                  onClick={() => {
                    setDisplayMode(PageDisplayMode.TABLE);
                  }}
                  p={2}
                >
                  <CustomTooltip
                    title={
                      "Switch to Scenarios Focus mode to display more scenarios at the same time"
                    }
                    placement="top"
                  >
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      gap={1}
                      alignItems={"center"}
                    >
                      <Typography variant="overline">
                        Show more scenarios at once
                      </Typography>
                      <SvgToIcon
                        IconLogo={icons.ExpandScreenIcon}
                        width={"16px"}
                      />
                    </Box>
                  </CustomTooltip>
                </Box>
              )}
              <ScenariosSetCards
                rows={currentRows}
                setSelectedScenarioSet={setSelectedScenarioSet}
                fullScreen={fullScreen}
                resetFilters={resetFilters}
              />
              {scenariosSetsRowsCard?.length > rowsPerPage && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 0,
                    right: 0,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    zIndex: 10,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      borderRadius: "20px",
                      padding: "4px 16px",
                      boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={(page) => {
                        setCurrentPage(page);
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Grid>

            {fullScreen && (
              <Grid item xs={4.8}>
                <Box display={"flex"} flexDirection={"column"} gap={2}>
                  <TopImpactSteps
                    filteredScenariosSets={filteredScenariosSets}
                  />
                  <TopImpactRemediations
                    filteredScenariosSets={filteredScenariosSets}
                  />
                </Box>
              </Grid>
            )}
            <StepDrawer
              selectedStepId={selectedStepId}
              onStepDrawerClose={() => {
                onStepDrawerClose();
              }}
              entityTypeFilter={entityTypeFilter}
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
